import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Loader2, Eye, EyeOff, Calendar, FileText, FolderLock, Sparkles } from 'lucide-react';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_99de4320-286b-45dc-9267-85b0447b3219/artifacts/ijyqyuec_LexAgenda.png';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_860f89f1-5bcd-47ca-9a42-2845ecac666a/artifacts/4om46ky3_LexAgenda-final_logo.png';

const FEATURES = [
  { icon: Calendar, title: 'Meetings & Governance', desc: 'Plan Board and Committee meetings, ASM, and SSM. Use the Governance Hub to share documents for review, conduct polls and voting, and manage collaborative approvals across all stakeholders' },
  { icon: FileText, title: 'Document Generation', desc: 'Generate notices, agendas, minutes, resolutions, and Secretary\u2019s Certificates from reusable templates' },
  { icon: FolderLock, title: 'Library & Records', desc: 'Manage a centralized document repository with Corporate Books, Meeting Docs, Governance files, Regulatory Docs, and Communications with classification levels' },
  { icon: Sparkles, title: 'AI-Assisted Drafting', desc: 'Use AI to help draft minutes, resolutions, and certificates under your control' },
];

const Register = () => {
  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '', full_name: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    if (formData.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register({ email: formData.email, password: formData.password, full_name: formData.full_name, role: 'corporate_secretary' });
      navigate('/onboarding');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="auth-page">
      {/* Features Panel — visible on all screens */}
      <div className="relative overflow-hidden lg:w-1/2 lg:flex">
        <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover grayscale brightness-[0.70]" />
        <div className="absolute inset-0" style={{ background: 'rgba(0,140,160,0.25)', mixBlendMode: 'overlay' }} />
        <div className="absolute inset-0" style={{ background: 'rgba(80,100,110,0.30)', mixBlendMode: 'multiply' }} />

        <div className="relative z-10 flex flex-col w-full px-6 py-8 lg:justify-end lg:p-10 xl:p-14">
          {/* Mobile: logo + branding */}
          <div className="flex flex-col items-center mb-5 lg:hidden">
            <img src={LOGO_URL} alt="LexAgenda" className="h-28 object-contain mb-3" />
            <h2 className="text-white text-lg font-bold text-center" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
              Corporate Governance Made Simple
            </h2>
          </div>

          <div className="space-y-2.5 lg:space-y-3">
            {FEATURES.map((f, i) => (
              <div
                key={i}
                className="flex flex-col rounded-2xl px-4 py-3 lg:px-5 lg:py-4"
                style={{
                  background: 'rgba(255,255,255,0.18)',
                  backdropFilter: 'blur(18px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(18px) saturate(180%)',
                  border: '1px solid rgba(255,255,255,0.30)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.35)',
                }}
              >
                <div className="flex items-center gap-2 rounded-xl px-2.5 py-1 lg:px-3 lg:py-1.5 mb-1" style={{ background: 'rgba(245,138,38,0.22)' }}>
                  <f.icon className="h-4 w-4 text-[#F58A26] shrink-0" />
                  <h3 className="font-bold text-base lg:text-xl text-[#F58A26]" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5), 0 0 2px rgba(0,0,0,0.3)' }}>{f.title}</h3>
                </div>
                <div className="pl-1">
                  <p className="text-white text-sm lg:text-base leading-relaxed font-bold" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.4)' }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-4 text-xs text-white/60 font-bold text-center lg:text-left" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
            Aligned with the Revised Corporation Code (RA 11232) and SEC regulations
          </p>
        </div>
      </div>

      {/* Register Panel */}
      <div className="flex-1 flex flex-col items-center justify-center bg-[#FAFBFC] px-6 py-10">
        {/* Logo — only visible on desktop */}
        <img
          src={LOGO_URL}
          alt="LexAgenda"
          className="hidden lg:block h-72 xl:h-80 mb-8 object-contain"
          data-testid="auth-logo"
        />

        <div className="w-full max-w-sm">
          {/* Tabs */}
          <div className="flex rounded-lg border border-[#E2E6EA] overflow-hidden mb-8" data-testid="auth-tabs">
            <Link to="/login" className="flex-1 py-2.5 text-center text-sm font-medium bg-[#F8F9FA] text-[#5A6570] hover:text-[#1A1D21] transition-colors"
              data-testid="tab-login">
              Login
            </Link>
            <div className="flex-1 py-2.5 text-center text-sm font-medium bg-white text-[#1A1D21] border-b-2 border-[#00A3A3]"
              data-testid="tab-register">
              Register
            </div>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-xl border border-[#E2E6EA] shadow-sm p-6">
            <h2 className="text-lg font-bold text-[#1A1D21]" data-testid="form-title">Create Account</h2>
            <p className="text-sm text-[#5A6570] mb-6">Start managing your corporate governance today</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="auth-error">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="full_name" className="text-sm font-medium text-[#1A1D21]">Full Name</Label>
                <Input id="full_name" name="full_name" type="text" placeholder="Juan dela Cruz"
                  value={formData.full_name} onChange={handleChange} required
                  data-testid="register-name-input" className="border-[#E2E6EA] focus:border-[#00A3A3] focus:ring-[#00A3A3] h-10" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium text-[#1A1D21]">Email</Label>
                <Input id="email" name="email" type="email" placeholder="your@email.com"
                  value={formData.email} onChange={handleChange} required
                  data-testid="register-email-input" className="border-[#E2E6EA] focus:border-[#00A3A3] focus:ring-[#00A3A3] h-10" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm font-medium text-[#1A1D21]">Password</Label>
                <div className="relative">
                  <Input id="password" name="password" type={showPassword ? 'text' : 'password'} placeholder="At least 6 characters"
                    value={formData.password} onChange={handleChange} required
                    data-testid="register-password-input" className="border-[#E2E6EA] focus:border-[#00A3A3] focus:ring-[#00A3A3] pr-10 h-10" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A6570] hover:text-[#1A1D21] transition-colors"
                    data-testid="register-password-toggle" tabIndex={-1}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-[#1A1D21]">Confirm Password</Label>
                <div className="relative">
                  <Input id="confirmPassword" name="confirmPassword" type={showConfirm ? 'text' : 'password'} placeholder="Re-enter your password"
                    value={formData.confirmPassword} onChange={handleChange} required
                    data-testid="register-confirm-password-input" className="border-[#E2E6EA] focus:border-[#00A3A3] focus:ring-[#00A3A3] pr-10 h-10" />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A6570] hover:text-[#1A1D21] transition-colors"
                    data-testid="register-confirm-password-toggle" tabIndex={-1}>
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full bg-[#00A3A3] hover:bg-[#008585] text-white h-10"
                disabled={loading} data-testid="register-submit-btn">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account...</> : 'Create Account'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
